import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { List } from '@mui/material';
import { GridContainer, CenterContent } from 'global/components/perform-grid';
import { useLocation } from 'react-router-dom';
import NavToTop from 'feature/nav/NavToTop';
import useStyles from './styles';
import { get, set, size, debounce, upperFirst } from 'global/lib/lodash';
import SideBar from './sidebar';
import TaskNotify from '../../task/TaskNotify';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { ScoreLoading } from 'global/components/skeleton-loader';
import sendData from 'redux/actions/sendData';
import { useDispatch } from 'react-redux';
import { addNewTask } from 'redux/actions/view';
import { wait } from 'global/utils';
import { useAccessEdition } from 'global/utils/edition';
import { awaitAll } from 'global/utils/awaitAll';
import { useUser } from 'global/hooks/useUser';
import { getItemType, getItemKey, getId, isGob, filterData, setFilters, getBuiltId } from './utils';
import { ActiveEdit } from './ActiveEdit';
import { SystemScore } from './SystemScore';
import FilterLogic from './FilterLogic';
import { SubScoreLauncher } from './SubScore/SubScoreLauncher';
import { stringObj } from 'global/utils';
import AssessmentSnackBar from './AssessmentSnackBar';
import clsx from 'clsx';
import { fixWeirdDateIssue } from 'global/components/form';
import { _size } from '../../../global/utils';

export * from './utils';

export const AUTOTAG = '__AUTO__';

const ReportAssessmentScoreContent = (props) => {
  const { assessment, scores, printView, scIndex, canShowScores, isCourse, onToggleEdit, isTemplate } = props;

  //state
  const [selectedItems, setSelectedItems] = useState({});
  const [showSelectBoxes, setShowSelectBoxes] = useState(false);
  const [scorecardIndex, setScorecardIndex] = useState(scIndex !== undefined ? scIndex : 0);
  let [allProcessedScoreData, setAllProcessedScoreData] = useState(get(scores, 'data', []));
  let [treedProcessedScoreData, setTreedProcessedScoreData] = useState([]);
  const [sidebarOpen, setSidebar] = useState(false);
  const [activeEdit, _setActiveEdit] = useState();
  const [searchString, setSearchString] = useState('');
  const [filter, setFilter] = useState({});

  //data
  const scorecards = get(assessment, 'data.scorecards', []);
  const activeSc = get(scorecards, scorecardIndex, {});
  const scid = get(activeSc, 'id');
  let scoredata = get(allProcessedScoreData, scid, []);
  const status = get(assessment, 'data.status', 'draft');
  const assessment_id = get(assessment, 'data.id');
  const realm = get(assessment, 'data.realm');
  const realm_id = get(realm, 'id');
  const userCanEdit = (() => {
    if (status === 'published') {
      return false;
    }

    if (props.userCanEdit) {
      return true;
    } else {
      return false;
    }
  })();

  useEffect(() => {
    console.log(get(activeSc, 'config'));
  }, [activeSc]);

  const hasSubScores = !isTemplate && !isCourse && get(activeSc, `config.hasSubScores`, false);

  const realmFilter = useMemo(() => {
    if (!realm || !hasSubScores) {
      return null;
    }

    const options = {
      division: 'community',
      community: 'lot',
      lot: 'room',
    };
    return {
      tier: get(options, realm.tier),
      parent_id: realm.tier == 'lot' ? undefined : realm.id,
    };
  }, [realm, activeSc]);

  const realms = useRetrieveData(
    { url: '/realms' },
    {
      useEffect: true,
      waitFor: realmFilter,
      build: ['realms'],
      params: {
        filter: realmFilter || {},
      },
    }
  );
  const realmsLoading = get(realms, 'loading');

  const assessmentLoading = get(assessment, 'loading');
  const loading = assessmentLoading || get(scores, 'loading');
  const error = get(scores, 'error', false);

  //hooks
  const location = useLocation();
  const { me } = useUser();
  const dispatch = useDispatch();
  const userCanLink = useAccessEdition(get(activeSc, `editionId`)) && get(filter, 'listGroup') !== 'location';
  const classes = useStyles();
  const assessmentTasks = useRetrieveData(
    { url: '/tasks' },
    {
      waitFor: assessment_id && realm_id ? undefined : null,
      build: ['tasks'],
      params: {
        filter: {
          assessment: assessment_id,
          realm: realm_id,
        },
        include: 'subScore',
      },
    }
  );
  const setCurrentAnchor = useCallback(
    debounce(
      (toShow, goTo = false) => {
        if (props.isReview) {
          return;
        }
        location.state = { ...location.state, id: getId(toShow) };
      },
      50,
      { leading: false }
    ),
    []
  );
  const setAlerts = useCallback((a) => {
    if (!!a) {
      location.state = { ...location.state, alert: get(a, 'message', ''), severity: get(a, 'severity', 'success') };
    } else {
      location.state = { ...location.state, alert: '', severity: '' };
    }
  }, []);

  //effects
  useEffect(() => {
    setAllProcessedScoreData(get(scores, 'data', []));
  }, [scores.data]);

  useEffect(() => {
    if (error) {
      setAlerts({
        message: `Error: ${error.message}`,
        severity: 'error',
      });
    }
  }, [error]);

  useEffect(() => {
    if (onToggleEdit) {
      onToggleEdit(activeEdit);
    }
  }, [activeEdit, onToggleEdit]);

  useEffect(() => {
    // If the user changes filters while in Edit Mode,
    // we're going to change their current active checklist
    // to a checklist that has filtered content
    if (activeEdit) {
      const agg = get(treedProcessedScoreData, '0.children', []).reduce((agg, item) => {
        get(item, 'children', []).forEach((cl) => {
          if (size(get(cl, 'children')) > 0) {
            if (getId(cl) === getId(get(activeEdit, 'pssvChecklist'))) {
              agg = false;
            } else if (agg === true) {
              agg = { pssvChecklist: cl, pssvSystem: item };
            }
          }
        });
        return agg;
      }, true);

      if (get(agg, 'pssvChecklist')) {
        setAlerts({
          message: `No items in ${get(activeEdit, 'pssvChecklist.name', 'this category')}, switching to ${get(agg, 'pssvChecklist.name', 'another category')}`,
          severity: 'success',
        });
        setActiveEdit(agg);
      }
    }
  }, [treedProcessedScoreData]);

  useEffect(() => {
    props.onChange && props.onChange({ allProcessedScoreData, treedProcessedScoreData });
  }, [allProcessedScoreData]);

  useEffect(() => {
    const startingpage = props.page;
    return () => {
      //scores doesn't get updated when switching tabs because we update the treedProcessedScoreData data manually.
      // so need to get latest data when we leave the page. but only if we are still in this assessment
      if (window.location.pathname.includes(startingpage) && scores) {
        scores.update();
      }
    };
  }, []);

  //actions
  const update = async () => {
    if (get(scores, 'update')) {
      await scores.update();
    }
  };

  const setActiveEdit = useCallback((p) => {
    if (!get(p, 'pssvChecklist')) {
      return _setActiveEdit();
    }
    if (get(p, 'scroll')) {
      document.getElementById('allscores').scrollIntoView(true);
      window.scrollBy(0, get(p, 'scrollBy', 48));
    }
    _setActiveEdit({
      pssvSystem: p.pssvSystem,
      pssvChecklist: p.pssvChecklist,
    });
  }, []);

  useEffect(() => {
    if (status === 'published') {
      setActiveEdit();
    }
  }, [status]);

  const setSidebarOpen = useCallback((val) => {
    document.getElementsByTagName('body')[0].removeAttribute('style');
    setSidebar(val);
  }, []);

  let processing = useRef(false).current;
  const updateScores = useCallback(
    async (d, needsFullUpdate) => {
      if (processing) {
        await wait();
        return await updateScores(d, needsFullUpdate);
      }

      try {
        processing = true;
        if (needsFullUpdate) {
          if (!loading) {
            // setAlerts({
            //     message: `Error: Failed to update correctly. Updating tree to ensure we are up to date. Please wait.`,
            //     severity: 'error',
            // });
            await update();
          }
          return;
        } else {
          const item = get(d, '0', d);
          const pssvCheckpoint = get(item, 'processedScorecardScoreValue');
          const pssvChecklist = get(pssvCheckpoint, 'parent');
          const pssvSystem = get(pssvChecklist, 'parent');
          const processedScorecardScore = get(pssvSystem, 'parent');
          const isGobs = isGob(pssvCheckpoint);

          let replaced = false;
          let newAll = scoredata;

          if (isGobs) {
            let found = false;
            newAll = scoredata.map((na) => {
              if (getId(na) === getId(pssvCheckpoint)) {
                found = true;
                na = {
                  ...na,
                  ...pssvCheckpoint,
                  pssvChecklist: { ...pssvCheckpoint.parent, parent: undefined, children: undefined },
                  parent: undefined,
                };
              }
              return na;
            });

            if (!found) {
              newAll = [
                ...newAll,
                {
                  ...pssvCheckpoint,
                  pssvChecklist: { ...pssvCheckpoint.parent, parent: undefined, children: undefined },
                  parent: undefined,
                },
              ];
            }
            replaced = true;
          } else {
            newAll = scoredata.map((dd) => {
              if (getId(dd) === getId(pssvChecklist) || getBuiltId(dd) === getBuiltId(pssvChecklist)) {
                console._log('found checklist', pssvChecklist, dd);
                dd = {
                  ...dd,
                  displayScore: get(pssvChecklist, 'displayScore', get(dd, 'displayScore')),
                  displayValue: get(pssvChecklist, 'displayValue', get(dd, 'displayValue')),
                  calculatedScore: get(pssvChecklist, 'calculatedScore', get(dd, 'calculatedScore')),
                  value: get(pssvChecklist, 'value', get(dd, 'value')),
                  children: undefined,
                  parent: undefined,
                  id: dd.id,
                  parentId: dd.parentId,
                };
                replaced = true;
              } else if (getId(dd) === getId(pssvSystem) || getBuiltId(dd) === getBuiltId(pssvSystem)) {
                console._log('found system', pssvSystem, dd);
                dd = {
                  ...dd,
                  ...pssvSystem,
                  id: dd.id,
                  parentId: dd.parentId,
                };
                replaced = true;
              } else if (getId(dd) === getId(processedScorecardScore) || getBuiltId(dd) === getBuiltId(processedScorecardScore)) {
                console._log('found Scorecard', processedScorecardScore, dd);
                dd = {
                  ...dd,
                  ...processedScorecardScore,
                  id: dd.id,
                };
                replaced = true;
              } else {
                if (getId(dd) === getId(pssvCheckpoint) || getBuiltId(dd) === getBuiltId(pssvCheckpoint)) {
                  dd = {
                    ...dd,
                    ...pssvCheckpoint,
                    parent: undefined,
                    id: dd.id,
                    parentId: dd.parentId,
                    filters: {
                      ...dd.filters,
                      ...setFilters(pssvCheckpoint),
                    },
                  };
                  replaced = true;
                }
              }
              return dd;
            });
          }

          scoredata = newAll;

          if (!replaced) {
            return await update();
          }

          const { hasChildren } = await filterData(newAll, {
            ...filter,
            search: searchString,
            activeEdit: activeEdit,
            assessment: assessment,
          });
          //need to do this because the state isn't necessarily getting updated before this method is called again, so the allProcessedScoredata is stale by that point
          treedProcessedScoreData = hasChildren;
          setTreedProcessedScoreData(hasChildren);
          allProcessedScoreData[activeSc.id] = scoredata;
          setAllProcessedScoreData({ ...allProcessedScoreData });
        }
      } catch (err) {
        throw err;
      } finally {
        processing = false;
      }
    },
    [filter, searchString, activeEdit, assessment, activeSc, loading, allProcessedScoreData, scoredata]
  );

  const isLocationView = get(filter, 'listGroup') === 'location';
  const isCategoryView = get(filter, 'listGroup') === 'category' || !isLocationView;

  useEffect(() => {
    setSelectedItems({});
  }, [filter.listGroup]);

  const itemPasses = useCallback(
    (item) => {
      if (isLocationView) {
        return true;
      } else if (isCategoryView && item.isSubScore) {
        return false;
      }

      return get(item, 'filters.act', true);
    },
    [isLocationView, isCategoryView]
  );

  const createKey = (item) => {
    const itemType = getItemType(item);
    return {
      ...item,
      itemType,
    };
  };

  const findParent = (it) => {
    const searchChildren = (children, id) => {
      const childs = children || [];
      let found = childs.find((c) => c.id == id);

      if (found) {
        return found;
      } else {
        for (let i = 0; i < childs.length; i++) {
          found = searchChildren(childs[i].children, id);
          if (found) {
            break;
          }
        }
      }
      return found;
    };

    return searchChildren(treedProcessedScoreData, it.parentId);
  };

  const selectItem = useCallback(
    (item) => {
      //#region check first
      if (item.value == null && !isLocationView) {
        return setAlerts({
          message: `Error: Not allowed to select an unscored item.`,
          severity: 'error',
        });
      }

      const itemType = getItemType(item);
      const passes = itemPasses(item);
      if (!passes) {
        return setAlerts({
          message: `Error: High scores and N/O scores must have notes or images attached in order to select.`,
          severity: 'error',
        });
      }
      //#endregion
      setShowSelectBoxes(true);

      let changed = 0;
      const allAdds = {};

      let selItems = { ...selectedItems };
      const itemKey = getItemKey(item);
      const exists = get(selItems, itemKey) || item.selectAll === false;

      const setAndAdd = (it, checklistScore, systemScore) => {
        [systemScore, checklistScore].forEach((obj) => {
          const key = getItemKey(obj);
          if (!get(allAdds, `${key}`)) {
            set(allAdds, `${key}.obj`, obj);
            set(allAdds, `${key}.max`, 0);
            set(allAdds, `${key}.count`, 0);
          }
          allAdds[key]['max'] += 1;
        });
        if (itemType === 'selectAll' || itemKey === getItemKey(systemScore) || itemKey === getItemKey(checklistScore) || itemKey === getItemKey(it)) {
          set(selItems, getItemKey(it), createKey(it));
          changed += 1;
        }
        if (get(selItems, getItemKey(it))) {
          allAdds[getItemKey(systemScore)]['count'] += 1;
          allAdds[getItemKey(checklistScore)]['count'] += 1;
        }
      };

      const updateParentItem = (it, changed, d = false) => {
        const keyId = getItemKey(it);
        if (d || get(selItems, `${keyId}.count`) === changed) {
          delete selItems[keyId];
        } else {
          set(selItems, `${keyId}.count`, get(selItems, `${keyId}.count`) - changed);
          set(selItems, `${keyId}.fullCount`, false);
        }
      };

      if (exists) {
        if (itemType === 'selectAll') {
          selItems = {};
          setShowSelectBoxes(false);
        } else {
          const isSystemTier = itemType === 'superset' || itemType === 'system';
          const isChecklistTier = itemType === 'checklist' || itemType === 'room';
          const systemTierId = isSystemTier ? `${itemType}_${getId(item)}` : `${isLocationView ? 'system' : 'superset'}_${get(item, 'parentId')}`;
          const checklistTierId = isChecklistTier ? `${itemType}_${getId(item)}` : `${isLocationView ? 'room' : 'checklist'}_${get(item, 'parentId')}`;
          get(treedProcessedScoreData, '0.children', []).some((sys) => {
            if (itemType === 'checkpoint' || getItemKey(sys) === systemTierId) {
              const bool = (sys.children || []).some((child) => {
                if (isSystemTier || getItemKey(child) === checklistTierId) {
                  (child.children || []).forEach((cchild) => {
                    // If this checkpoint passes,
                    // and if selected item is a checkpoint, must also be the selected checkpoint
                    if (itemPasses(cchild) && (itemType !== 'checkpoint' || getItemKey(cchild) === itemKey)) {
                      delete selItems[getItemKey(cchild)];
                      changed += 1;
                    }
                  });
                  updateParentItem(child, changed, itemType !== 'checkpoint');
                  if (!isSystemTier) return true;
                }
              });
              updateParentItem(sys, changed, isSystemTier);
              if (isSystemTier) return true;
              return bool;
            }
          });
        }
      } else {
        get(treedProcessedScoreData, '0.children', []).forEach((sys) => {
          (sys.children || []).forEach((child) => {
            (child.children || []).forEach((cchild) => {
              if (itemPasses(cchild)) {
                setAndAdd(cchild, child, sys);
              }
            });
          });
        });
        Object.keys(allAdds).forEach((s) => {
          if (get(allAdds, `${s}.count`, 0) > 0) {
            set(
              selItems,
              s,
              createKey({
                ...get(allAdds, `${s}.obj`),
                fullCount: get(allAdds, `${s}.max`, 0) === get(allAdds, `${s}.count`, 0),
                count: get(allAdds, `${s}.count`, 0),
                max: get(allAdds, `${s}.max`, 0),
              })
            );
          }
        });
      }

      if (changed === 0 && !exists) {
        if (!_size(selItems)) {
          setShowSelectBoxes(false);
        }
        return setAlerts({
          message: `Nothing to select in this section.`,
          severity: 'error',
        });
      } else {
        if (changed > 0 && !exists) {
          setAlerts({
            message: `You added ${changed} new item${changed > 1 ? 's' : ''}.`,
          });
        }
        setSelectedItems(selItems);
        if (!_size(selItems)) {
          setShowSelectBoxes(false);
        }
      }
    },
    [selectedItems, treedProcessedScoreData]
  );

  const tv = get(treedProcessedScoreData, '0.children', []);
  const processedOverallScores = scorecards.map((s) => get(allProcessedScoreData, `${s?.id}.0`, []));

  const createTasks = async (formState, items) => {
    const pssvCheckpointsSelected = Object.keys(selectedItems)
      .filter((k) => get(selectedItems, [k, 'itemType']) === 'checkpoint')
      .map((cp) => get(selectedItems, cp));

    const order = []; //put the checkpoint scores in order so that the PDF renders them correctly
    const pushOrder = (check) => {
      if (size(check.subscores)) {
        check.subscores.map((ss) => order.push(ss));
      } else {
        order.push(check);
      }
    };
    (tv || []).map((sys) => {
      const childs = get(sys, 'children', []);
      childs.map((child) => {
        const check = pssvCheckpointsSelected.find((cp) => getId(cp) === getId(child));
        if (check) {
          pushOrder(check);
        } else if (child.children) {
          child.children.map((_child) => {
            const _check = pssvCheckpointsSelected.find((cp) => getId(cp) === getId(_child));
            if (_check) {
              pushOrder(_check);
            }
          });
        }
      });
    });

    await awaitAll(
      order.map((pssvCheckpoint) => () => async () => {
        const { isSubScore } = pssvCheckpoint;
        const pssvChecklist = get(pssvCheckpoint, 'checklist', findParent(pssvCheckpoint)) || findParent(pssvCheckpoint) || pssvCheckpoint.checklist;
        if (!isSubScore || (isSubScore && get(pssvCheckpoint, 'notes') !== AUTOTAG && (!!get(pssvCheckpoint, 'notes') || !!size(get(pssvCheckpoint, 'images', []))))) {
          const hasItem = get(items, get(pssvChecklist, 'checklistId', get(pssvCheckpoint, 'checklist.id')));

          let tradeTypes = [];
          if (hasItem) {
            tradeTypes = get(
              get(hasItem, 'checkpoints', []).find((c) => c.id === pssvCheckpoint.id),
              'tradeTypes',
              []
            );
          }

          const isGobs = isGob(pssvCheckpoint);

          const getName = () => {
            if (isGobs) {
              return `${get(pssvChecklist, 'name', '')}: General Observations`;
            } else if (get(pssvCheckpoint, 'realm.tier') === 'room') {
              return `${get(pssvCheckpoint, 'realm.name')} - ${get(pssvChecklist, 'name', '')}: ${get(pssvCheckpoint, 'name')}`;
            } else {
              return `${get(pssvChecklist, 'name', '')}: ${get(pssvCheckpoint, 'name')}`;
            }
          };

          const createdTask = await sendData(
            {
              url: `/tasks?include=images,assignees,viewers,authors,comments,comments.author,comments.images,realm,scorecardScore,scorecardScore.checklist,scorecardScore.checkpoint,scorecardScore.checkpoint.checkpointOptions,tradeTypes`,
              method: 'post',
              data: {
                data: {
                  type: 'tasks',
                  attributes: stringObj({
                    name: getName(),
                    description: isGobs ? get(pssvCheckpoint, 'criteria') : get(pssvCheckpoint, 'notes'),
                    status: 0,
                    realm_id,
                    assessment_id,
                    scorecard_score_id: !isSubScore ? get(pssvCheckpoint, 'scorecardScoreId', '') : undefined,
                    sub_score_id: isSubScore ? pssvCheckpoint.id : undefined,
                    checkpoint_id: isGobs ? null : get(pssvCheckpoint, 'checkpointId', ''),
                    due_date: fixWeirdDateIssue(get(formState, 'dueDate'), true),
                    meta: get(formState, 'meta', {}),
                  }),
                  relationships: {
                    authors: {
                      data: [stringObj({ type: 'users', role: 'author', id: me.id })],
                    },
                    assignees: {
                      data: get(formState, 'assignedTo', []).map((a) =>
                        stringObj({
                          type: 'users',
                          role: 'assignee',
                          id: a.id,
                        })
                      ),
                    },
                    tradeTypes: size(tradeTypes)
                      ? {
                          data: (tradeTypes || []).map((t, i) => ({
                            type: 'trade-types',
                            id: t.id,
                            pivot: { order: `${i}` },
                          })),
                        }
                      : undefined,
                  },
                },
              },
            },
            { normalize: { build: ['tasks'] } }
          );

          const task = get(createdTask, '0');
          dispatch(addNewTask(task));
          return task;
        }
      }),
      { queueSize: 1 }
    );

    setSelectedItems({});
    setShowSelectBoxes(false);
    assessmentTasks.update();
  };

  const onReview = () => {
    setFilter((f) => ({ ...f, listGroup: 'category' }));
  };

  if (window.SUPPORT) {
    window.UPDATE = update;
  }

  const subScoreProps = useMemo(
    () => ({
      scorecard: activeSc,
      realms: {
        ...realms,
        update: (e) => {
          realms.update(e);
          get(window, 'MASTERREALM.update', () => 0)();
        },
        realmFilter,
      },
      allScoreData: scoredata,
      updateScores,
      assessment,
      setAlerts,
      activeEdit,
      hasSubScores,
      filter,
      onReview,
    }),
    [filter, activeEdit, assessment, scoredata, realmsLoading, activeSc]
  );

  if (!canShowScores) {
    if (assessmentLoading) {
      return (
        <GridContainer>
          <CenterContent fullWidth={printView} selfCenter>
            <div style={{ padding: 12 }}>
              <ScoreLoading />
            </div>
          </CenterContent>
        </GridContainer>
      );
    }
    return (
      <GridContainer>
        <CenterContent fullWidth={printView} selfCenter>
          <div style={{ padding: 12 }}>{`You don't have access to scores while this report is in ${upperFirst(status)}.`}</div>
        </CenterContent>
      </GridContainer>
    );
  }

  return (
    <>
      <NavToTop />
      <div className={clsx(classes.wrapper, isCourse ? classes.wrapperCourse : undefined)} style={{ flexDirection: printView ? 'column' : 'row' }}>
        {!printView && !isCourse && (
          <SideBar
            printView={printView}
            open={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            pssvSystems={tv}
            processedOverallScores={processedOverallScores}
            loading={loading}
            canShowScores={canShowScores}
            setActiveEdit={setActiveEdit}
            activeEdit={activeEdit}
            scorecardIndex={scorecardIndex}
            filter={filter}
            scorecard={activeSc}
          />
        )}
        <div id={'RightPanel'} className={clsx(classes.right, printView ? classes.rightPrint : undefined, isCourse ? classes.rightCourse : undefined)}>
          <FilterLogic
            {...{
              isCourse,
              isTemplate,
              assessment,
              activeSc,
              scorecardIndex,
              assessmentLoading,
              loading,
              status,
              updateScores: update,
              scoredata,
              processedOverallScores,
              setTreedProcessedScoreData,
              activeEdit,
              scorecards,
              setScorecardIndex,
              filter,
              setFilter,
              searchString,
              setSearchString,
              hasSubScores,
              updateStatusFilter: props.updateStatusFilter,
              scores,

              taskBanner: {
                visible: !activeEdit && !isCourse && !printView,
                disabled: false,
                userCanEdit,
                realm_id,
                selectItem,
                itemPasses,
                selectedItems,
                onCreateTasks: createTasks,
                allItems: tv,
                showSelectBoxes,
                setShowSelectBoxes,
                assessment: assessment.data,
                setSelectedItems,
              },
            }}
          />

          {!printView && !isCourse && (
            <>
              <TaskNotify realmId={realm_id || null} />
              <NavToTop />
            </>
          )}

          {userCanEdit && !activeEdit && !isTemplate && !printView && <SubScoreLauncher {...subScoreProps} />}

          <List id={'allscores'}>
            {!loading && !size(tv) && !!size(filter) && !printView && <div>There are no visible scores. Try changing the filters.</div>}
            {!activeEdit && loading && !!size(filter) && <ScoreLoading />}
            {!activeEdit &&
              !!size(filter) &&
              !loading &&
              tv.map((d, i) => {
                return (
                  <SystemScore
                    {...props}
                    key={d.id}
                    notFirst={i !== 0}
                    canShowScores={canShowScores}
                    userCanEdit={userCanEdit}
                    userCanModify={props.userCanModify}
                    assessment={assessment}
                    pssvSystem={d}
                    assessmentTasks={assessmentTasks.data}
                    updateAssessmentTasks={get(assessmentTasks, 'update')}
                    pssvSystemIndex={i}
                    next={get(tv, i + 1)}
                    prev={get(tv, i - 1)}
                    setCurrentAnchor={setCurrentAnchor}
                    setSidebarOpen={setSidebarOpen}
                    setActiveEdit={setActiveEdit}
                    selectItem={selectItem}
                    selectedItems={selectedItems}
                    showSelectBoxes={showSelectBoxes}
                    filter={filter}
                    printView={printView}
                    scorecard={get(scorecards, scorecardIndex)}
                    userCanLink={userCanLink}
                    isCourse={isCourse}
                    updateScores={updateScores}
                    searchString={searchString}
                    {...subScoreProps}
                  />
                );
              })}

            {activeEdit && size(tv) > 0 && (
              <ActiveEdit
                isCourse={isCourse}
                isTemplate={isTemplate}
                activeEdit={activeEdit}
                assessment={assessment}
                assessmentTasks={assessmentTasks}
                setActiveEdit={setActiveEdit}
                setSidebarOpen={setSidebarOpen}
                updateScores={updateScores}
                setCurrentAnchor={setCurrentAnchor}
                selectedItems={selectedItems}
                treedProcessedScoreData={tv}
                filter={filter}
                scorecard={get(scorecards, scorecardIndex)}
                {...subScoreProps}
              />
            )}
          </List>
          <div style={{ height: '3rem' }}></div>
        </div>

        <AssessmentSnackBar selectedItems={selectedItems} activeEdit={activeEdit} />
      </div>
    </>
  );
};

export default ReportAssessmentScoreContent;
